import {
  LINK_IN_BIO_BLOCK_STYLES,
  LINK_IN_BIO_BLOCK_TYPES,
  LINK_IN_BIO_LIMITS,
  LINK_IN_BIO_ALIGNS,
  LINK_IN_BIO_LOOKS,
  LINK_IN_BIO_SOCIAL_ERRORS,
  LINK_IN_BIO_SOCIAL_PLATFORMS,
  normalizeLinkInBioUrl,
  toLinkInBioSocialUrl,
} from '@repo/lib';
import { z } from '../zod';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { BadgeIconEnumValidationSchema } from '../product/enum';

const optionalText = (max: number) => z.string().trim().max(max).nullish();

export const LinkInBioLookValidationSchema = z.enum(LINK_IN_BIO_LOOKS);
export const LinkInBioSocialPlatformValidationSchema = z.enum(LINK_IN_BIO_SOCIAL_PLATFORMS);
export const LinkInBioBlockTypeValidationSchema = z.enum(LINK_IN_BIO_BLOCK_TYPES);
export const LinkInBioBlockStyleValidationSchema = z.enum(LINK_IN_BIO_BLOCK_STYLES);

export const LinkInBioMediaValidationSchema = z.object({
  file: z.string().nullish(),
  url: z.url({ protocol: /^https$/ }).max(LINK_IN_BIO_LIMITS.url),
  name: z.string().max(255).nullish(),
  size: z.number().nullish(),
  width: z.number().nullish(),
  height: z.number().nullish(),
});

export const LinkInBioSocialValidationSchema = z
  .object({
    platform: LinkInBioSocialPlatformValidationSchema,
    url: z.string().trim().min(1).max(LINK_IN_BIO_LIMITS.url),
  })
  .superRefine((social, ctx) => {
    if (!toLinkInBioSocialUrl(social.platform, social.url)) {
      ctx.addIssue({
        code: 'custom',
        path: ['url'],
        message: LINK_IN_BIO_SOCIAL_ERRORS[social.platform],
      });
    }
  });

export const LinkInBioBlockValidationSchema = z
  .object({
    id: z.string().nullish(),
    type: LinkInBioBlockTypeValidationSchema,
    title: optionalText(LINK_IN_BIO_LIMITS.blockTitle),
    url: z.string().trim().max(LINK_IN_BIO_LIMITS.url).nullish(),
    page: ObjectIdStringValidationSchema.nullish(),
    limit: z
      .number()
      .int()
      .min(LINK_IN_BIO_LIMITS.eventsMin)
      .max(LINK_IN_BIO_LIMITS.eventsMax)
      .nullish(),
    thumbnail: LinkInBioMediaValidationSchema.nullish(),
    icon: BadgeIconEnumValidationSchema.nullish(),
    showFrom: z.iso.datetime({ offset: true }).nullish(),
    showUntil: z.iso.datetime({ offset: true }).nullish(),
    style: LinkInBioBlockStyleValidationSchema.default('button'),
    hidden: z.boolean().default(false),
  })
  .superRefine((block, ctx) => {
    const needsTitle = block.type === 'link' || block.type === 'heading';
    if (needsTitle && !block.title) {
      ctx.addIssue({ code: 'custom', path: ['title'], message: 'Add a title' });
    }
    if (block.type === 'link' && !normalizeLinkInBioUrl(block.url)) {
      ctx.addIssue({ code: 'custom', path: ['url'], message: 'Enter a web address' });
    }
    if (block.type === 'page' && !block.page) {
      ctx.addIssue({ code: 'custom', path: ['page'], message: 'Choose a page' });
    }
    if (
      block.showFrom &&
      block.showUntil &&
      new Date(block.showUntil).getTime() <= new Date(block.showFrom).getTime()
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['showUntil'],
        message: 'Show until must be after show from',
      });
    }
  });

const linkInBioShape = {
  published: z.boolean().default(false),
  title: optionalText(LINK_IN_BIO_LIMITS.title),
  bio: optionalText(LINK_IN_BIO_LIMITS.bio),
  avatar: LinkInBioMediaValidationSchema.nullish(),
  look: LinkInBioLookValidationSchema.default('theme'),
  theme: ObjectIdStringValidationSchema.nullish(),
  align: z.enum(LINK_IN_BIO_ALIGNS).default('center'),
  showCheckoutPageBranding: z.boolean().nullish(),
  socials: z.array(LinkInBioSocialValidationSchema).max(LINK_IN_BIO_LIMITS.socials).default([]),
  blocks: z.array(LinkInBioBlockValidationSchema).max(LINK_IN_BIO_LIMITS.blocks).default([]),
};

export const UpdateLinkInBioRequestValidationSchema = z
  .object(linkInBioShape)
  .strict()
  .meta({ id: 'UpdateLinkInBioRequest' });

export type UpdateLinkInBioRequest = z.infer<typeof UpdateLinkInBioRequestValidationSchema>;

const LinkInBioMediaResponseSchema = z.object({
  file: z.string().nullable(),
  url: z.string(),
  name: z.string().nullable(),
  size: z.number().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
});

export const LinkInBioResponseValidationSchema = z
  .object({
    published: z.boolean(),
    title: z.string().nullable(),
    bio: z.string().nullable(),
    avatar: LinkInBioMediaResponseSchema.nullable(),
    look: LinkInBioLookValidationSchema,
    theme: z.string().nullable(),
    align: z.enum(LINK_IN_BIO_ALIGNS),
    showCheckoutPageBranding: z.boolean().nullable(),
    socials: z.array(
      z.object({ platform: LinkInBioSocialPlatformValidationSchema, url: z.string() }),
    ),
    blocks: z.array(
      z.object({
        id: z.string(),
        type: LinkInBioBlockTypeValidationSchema,
        title: z.string().nullable(),
        url: z.string().nullable(),
        page: z.string().nullable(),
        limit: z.number().nullable(),
        thumbnail: LinkInBioMediaResponseSchema.nullable(),
        icon: z.string().nullable(),
        showFrom: z.string().nullable(),
        showUntil: z.string().nullable(),
        style: LinkInBioBlockStyleValidationSchema,
        hidden: z.boolean(),
      }),
    ),
  })
  .meta({ id: 'LinkInBio' });

export const LinkInBioPageSummaryValidationSchema = z
  .object({
    id: z.string(),
    type: z.enum(['checkout', 'event', 'form']),
    slug: z.string(),
    status: z.enum(['published', 'draft', 'archived']),
    testmode: z.boolean(),
    title: z.string(),
    imageUrl: z.string().nullable(),
    event: z
      .object({
        startDate: z.string().nullable(),
        endDate: z.string().nullable(),
        timezone: z.string().nullable(),
        status: z.enum(['active', 'sold_out', 'canceled']).nullable(),
        location: z.string().nullable(),
        type: z.enum(['in_person', 'virtual', 'hybrid']).nullable(),
      })
      .nullable(),
  })
  .meta({ id: 'LinkInBioPageSummary' });

export const GetLinkInBioResponseValidationSchema = z.object({
  linkInBio: LinkInBioResponseValidationSchema,
  pages: z.array(LinkInBioPageSummaryValidationSchema),
  upcomingEvents: z.array(LinkInBioPageSummaryValidationSchema),
});

export const GetLinkInBioPagesQueryValidationSchema = z.object({
  ids: z
    .string()
    .transform(value =>
      value
        .split(',')
        .map(id => id.trim())
        .filter(Boolean),
    )
    .pipe(z.array(ObjectIdStringValidationSchema).min(1).max(LINK_IN_BIO_LIMITS.blocks)),
});

export const GetLinkInBioPagesResponseValidationSchema = z.object({
  pages: z.array(LinkInBioPageSummaryValidationSchema),
});
